/**
 * Kartenraster für die beiden Ankreuzkataloge des Schritts „Bauausführung":
 * besondere Ausstattungen/Sonderbestandteile und Wellness-Einrichtungen.
 *
 * Vorher elf Zeilen mit Kontrollkästchen und Beschriftung. Jeder Eintrag ist
 * aber ein Bauteil mit eigenem Aussehen (Aufzug, Rolltreppe, Trafo,
 * Schwimmbad …) — am Bild sind sie schneller zu treffen als am Wort, und der
 * Schritt bleibt in der Bildsprache von Bauartklasse, Dach, Stellung und
 * Stellplätzen. Die Machart ist die des `StellplatzBlock`: Bildkarte als
 * `role="checkbox"`, Folgefragen klappen unter der jeweils gewählten Karte auf.
 *
 * **Gespeichert wird unverändert.** Je Eintrag ein Objekt unter seinem
 * Katalogschlüssel:
 *
 *   sonderausstattungen: { personenaufzug: { selected: "true", wert: "25000" } }
 *
 * `selected` bleibt der String „true"/„false" (nicht boolean), und das Abwählen
 * löscht die Zusatzangaben weiterhin NICHT — beides genau wie in der
 * abgelösten Ankreuzliste, damit Bestandsdaten unberührt bleiben.
 */
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { ILLU } from "./illustrationTokens";
import { AusstattungIcon, type AusstattungMotiv } from "./ausstattungBilder";
import { ziffernFilter } from "./GrossZahlEingabe";

/** Untergrenze einer Karte — Begründung am Stil `grid`. */
const KARTE_MIN_PX = 300;

/**
 * Beschriftung eines Zusatzfeldes. Stand bis hierher als `labelForSonder` in
 * `pages/RisikoImmobilieStepForm.tsx`; sie gehört dorthin, wo die Felder
 * gezeichnet werden. Der `default`-Zweig gibt den Schlüssel zurück, damit ein
 * neu ergänztes Zusatzfeld sichtbar unbeschriftet ist statt unsichtbar.
 */
export const labelFuerZusatzfeld = (key: string): string => {
  switch (key) {
    case "wert":
      return "Wert (€)";
    case "einbaudatum":
      return "Einbaudatum";
    case "was":
      return "Was?";
    case "inFlaeche":
      return "In Flächenberechnung enthalten?";
    case "anteilProzent":
      return "Anteil (%)";
    case "lage":
      return "Lage (innen / außen)";
    default:
      return key;
  }
};

const useStyles = makeStyles({
  /**
   * Der Umbruch kommt aus der GEMESSENEN Rasterbreite, nicht aus dem Viewport
   * (das Formular läuft auch in einem schwebenden Fenster, das schmal gezogen
   * werden kann, während der Viewport breit bleibt) — dieselbe Regel wie im
   * `BauartklassePicker`. Bei der auf 920 px gedeckelten Inhaltsspalte und
   * 12 px Abstand ergeben 300 px Untergrenze höchstens zwei Spalten
   * (3 x 300 + 2 x 12 = 924 > 920): so bleibt neben dem 72 px breiten Bild
   * genug Platz für Beschriftungen wie „E-Ladestationen / Wallboxen".
   *
   * `min(300px, 100%)` statt `300px`, damit das Raster einen schmaleren
   * Container nicht überläuft. `alignItems: start`, damit eine aufgeklappte
   * Karte ihre Nachbarin nicht mitwachsen lässt.
   */
  grid: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${KARTE_MIN_PX}px, 100%), 1fr))`,
    gap: "12px",
    alignItems: "start",
  },
  karte: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  // Der Rahmen sitzt an der Bildkarte selbst; die Hülle hält nur Karte und
  // Folgefragen zusammen (wie im StellplatzBlock — zwei ineinanderliegende
  // Rahmen machen den Aktiv-Zustand schwerer lesbar).
  kopf: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "left",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "14px",
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
  kopfAktiv: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  kopfBild: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: ILLU.headerBg,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("8px"),
  },
  kopfSvg: {
    width: "72px",
    height: "58px",
  },
  kopfLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    lineHeight: 1.3,
    color: BRAND.ink,
    flexGrow: 1,
    minWidth: 0,
  },
  // Auch die Felder brechen nach der Kartenbreite um, nicht nach dem Viewport.
  detail: {
    marginTop: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(160px, 100%), 1fr))",
    gap: "10px",
  },
  feld: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    minWidth: 0,
  },
  feldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  // `boxSizing` ist Pflicht: in apps/web gilt border-box nur unter `.kvp-rd`,
  // ohne die Angabe liefe `width: 100%` plus Polsterung über die Karte hinaus.
  eingabe: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
});

/**
 * Ein Katalogeintrag. `key` ist zugleich der Motiv-Schlüssel: `MOTIVE` in
 * `ausstattungBilder.tsx` ist über denselben Typ vollständig erzwungen, ein
 * neuer Eintrag ohne Zeichnung fällt also beim Type-Check auf.
 */
export type AusstattungsEintrag = {
  key: AusstattungMotiv;
  label: string;
  extra: readonly string[];
};

type Props = {
  /** Vorlesename der Gruppe, z. B. „Besondere Ausstattungen". */
  gruppe: string;
  /** Präfix der `name`-Attribute, z. B. `sonderausstattung`. */
  feldPraefix: string;
  eintraege: readonly AusstattungsEintrag[];
  /** Der komplette Antwortwert (`sonderausstattungen` bzw. `wellness`). */
  werte: Record<string, Record<string, string>>;
  /** Schreibt EIN Unterfeld eines Eintrags — ein Aufruf je Bedienung. */
  onSet: (key: string, feld: string, wert: string) => void;
};

const AusstattungsKatalog = ({ gruppe, feldPraefix, eintraege, werte, onSet }: Props) => {
  const styles = useStyles();

  return (
    <div className={styles.grid} role="group" aria-label={gruppe}>
      {eintraege.map((eintrag) => {
        const eintragWerte = werte[eintrag.key] ?? {};
        const gewaehlt = eintragWerte.selected === "true";
        return (
          <div key={eintrag.key} className={styles.karte}>
            <button
              type="button"
              role="checkbox"
              aria-checked={gewaehlt}
              className={mergeClasses(styles.kopf, gewaehlt && styles.kopfAktiv)}
              onClick={() => onSet(eintrag.key, "selected", gewaehlt ? "false" : "true")}
            >
              <span className={styles.kopfBild}>
                <AusstattungIcon motiv={eintrag.key} className={styles.kopfSvg} />
              </span>
              <span className={styles.kopfLabel}>{eintrag.label}</span>
            </button>
            {gewaehlt && (
              <div className={styles.detail}>
                {eintrag.extra.map((feld) => {
                  const beschriftung = labelFuerZusatzfeld(feld);
                  const name = `${feldPraefix}-${eintrag.key}-${feld}`;
                  return (
                    <div key={feld} className={styles.feld}>
                      {/* Die Beschriftung steht NEBEN dem Feld, nicht in einem
                          umschließenden <label> — sonst würde sie Teil des
                          Feldnamens. Den Namen trägt `aria-label`, und er nennt
                          zusätzlich den Eintrag: „Aufzug — Wert (€)" ist auch
                          außerhalb der Karte eindeutig. */}
                      <span className={styles.feldLabel}>{beschriftung}</span>
                      {feld === "inFlaeche" ? (
                        <select
                          className={styles.eingabe}
                          name={name}
                          aria-label={`${eintrag.label} — ${beschriftung}`}
                          value={eintragWerte[feld] ?? ""}
                          onChange={(e) => onSet(eintrag.key, feld, e.target.value)}
                        >
                          <option value="">— wählen —</option>
                          <option value="ja">Ja</option>
                          <option value="nein">Nein</option>
                        </select>
                      ) : (
                        <input
                          className={styles.eingabe}
                          name={name}
                          aria-label={`${eintrag.label} — ${beschriftung}`}
                          inputMode={feld === "wert" || feld === "anteilProzent" ? "decimal" : undefined}
                          value={eintragWerte[feld] ?? ""}
                          placeholder={feld === "wert" ? "0,00" : undefined}
                          onChange={(e) =>
                            onSet(
                              eintrag.key,
                              feld,
                              feld === "wert" || feld === "anteilProzent"
                                ? ziffernFilter(e.target.value, { dezimal: true })
                                : e.target.value,
                            )
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AusstattungsKatalog;
